import React, { useEffect, useState, useRef } from 'react';
import MainSideBar from './MainSideBar';
import Text from 'atoms/Text';
import Button from 'atoms/Button';
import { toast } from 'react-toastify';
import { socketEmit } from 'services/socket';
import { getAuth } from 'services/auth.service';

const SubFundSidebar = ({ showSidebar, setShowSidebar, subFunds = [], onFundsUpdated }) => {
    const [funds, setFunds] = useState([]);
    const [initialFunds, setInitialFunds] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [errorKeys, setErrorKeys] = useState([]);
    const [validationActive, setValidationActive] = useState(false);
    const userId = getAuth()?.id;

    const inputRefs = useRef([]);
    const sidebarRef = useRef(null);

    useEffect(() => {
        if (showSidebar) {
            setFunds(subFunds.map(f => ({ ...f })));
            setInitialFunds(subFunds.map(f => ({ ...f })));
            setHasChanges(false);
            setEditingIndex(null);
            setErrorKeys([]);
            setValidationActive(false);
            inputRefs.current = inputRefs.current.slice(0, subFunds.length);
        }
    }, [showSidebar, subFunds]);

    const handleChange = (index, value) => {
        let numeric = value.replace('%', '').trim();
        if (!/^\d*\.?\d{0,2}$/.test(numeric)) return;
        if (numeric !== '') {
            numeric = String(parseFloat(numeric));
        }
        const updated = [...funds];
        updated[index].allocation = numeric;
        setFunds(updated);
        setHasChanges(JSON.stringify(updated) !== JSON.stringify(initialFunds));

        if (errorKeys.includes(index)) {
            setErrorKeys([]);
        }
    };

    const handleCancel = () => {
        setFunds(initialFunds.map(f => ({ ...f })));
        setHasChanges(false);
        setEditingIndex(null);
        setErrorKeys([]);
        setValidationActive(false);
    };

    const handleSave = () => {
        setValidationActive(true);
        const total = funds.reduce((sum, f) => sum + (parseFloat(f.allocation) || 0), 0);
        if (Math.abs(total - 100) > 0.0001) {
            setErrorKeys(funds.map((_, idx) => idx));
            return;
        }
        setErrorKeys([]);

        const payload = funds.map(f => ({
            id: f.userSubFundSplitId,
            subFundId: f.subFundId,
            allocation: Number(f.allocation),
            userId,
        }));

        setIsSaving(true);
        socketEmit('/put/user-sub-fund-splits', { subfunds: payload });
        toast.success('Fund Allocation Updated Successfully');
        setIsSaving(false);
        setHasChanges(false);
        setInitialFunds(funds.map(f => ({ ...f })));
        if (onFundsUpdated) onFundsUpdated(funds.map(f => ({ ...f })));
        setShowSidebar(false);
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const nextIndex = index + 1;
            if (nextIndex < funds.length && inputRefs.current[nextIndex]) {
                inputRefs.current[nextIndex].focus();
            }
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                setShowSidebar(false);
            }
        };
        if (showSidebar) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showSidebar, setShowSidebar]);

    return (
        <div ref={sidebarRef}>
            <MainSideBar heading="Manage Fund Allocation" setShowSidebar={setShowSidebar} showSidebar={showSidebar}>
                <div className="pb-3 border-b border-gray-500 mb-1 max-h-[calc(100vh-140px)] overflow-auto">
                    {funds.map((fund, index) => {
                        const isEditing = editingIndex === index;
                        const hasError = validationActive && errorKeys.includes(index);
                        return (
                            <div key={fund.id || index} className="flex">
                                <Text
                                    variant="caption"
                                    fontWeight="font-semibold"
                                    className={`text-gray-100 bg-gray-600 py-1.5 px-2 border border-r-0 border-gray-500 basis-2/3
                                    ${index !== 0 ? '!border-t-0' : ''}`}
                                >
                                    {fund.nickName || fund.name}
                                </Text>
                                <input
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    type="text"
                                    value={isEditing ? fund.allocation : (fund.allocation || 0) + '%'}
                                    onChange={(e) => handleChange(index, e.target.value)}
                                    onFocus={() => setEditingIndex(index)}
                                    onBlur={() => setEditingIndex(null)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    className={`w-full basis-1/3 py-1.5 h-[30px] px-2 bg-transparent text-center text-xs border  ${hasError
                                        ? "border-red-500"
                                        : isEditing
                                            ? "border-primary-300"
                                            : "border-gray-500"
                                        } text-gray-100 focus:outline-none 
                                ${index !== 0 && !isEditing ? '!border-t-0' : ''}
                                `}
                                />
                            </div>
                        );
                    })}
                    {validationActive && errorKeys.length > 0 && (
                        <div className="text-red-500 text-xs mt-2">
                            The combined allocation must equal 100%
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2 mt-2">
                    <Button
                        size="tiny"
                        label="Cancel"
                        type="ghost"
                        onClick={handleCancel}
                        disabled={!hasChanges}
                        className="!px-4"
                    />
                    <Button
                        size="tiny"
                        label="Save"
                        onClick={handleSave}
                        disabled={!hasChanges || isSaving}
                        className="!px-4"
                    />
                </div>
            </MainSideBar>
        </div>
    );
};

export default SubFundSidebar;
